import { useState, useRef, useCallback, useEffect } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  lang?: 'uz' | 'ru';
}

const EMOJI_LIST = [
  '😀','😃','😄','😁','😆','😅','😂','🤣','😊','😇',
  '🙂','🙃','😉','😌','😍','🥰','😘','😗','😙','😚',
  '😋','😛','😝','😜','🤪','🤨','🧐','🤓','😎','🥸',
  '🤩','🥳','😏','😒','😞','😔','😟','😕','🙁','☹️',
  '😣','😖','😫','😩','🥺','😢','😭','😤','😠','😡',
  '🤬','🤯','😳','🥵','🥶','😱','😨','😰','😥','😓',
  '🤗','🤔','🤭','🤫','🤥','😶','😐','😑','😬','🙄',
  '😯','😦','😧','😮','😲','🥱','😴','🤤','😪','😵',
  '👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞',
  '🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍',
  '👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝',
  '🙏','✍️','💅','🤳','💪','🦾','🦿','🦵','🦶','👂',
  '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔',
  '❣️','💕','💞','💓','💗','💖','💘','💝','💟','☮️',
  '⭐','🌟','✨','💫','🔥','💥','❄️','🌈','☀️','🌙',
  '🎉','🎊','🎈','🎁','🎀','🏆','🥇','🎯','🎮','🎲',
  '🌸','🌺','🌻','🌹','🌷','🍀','🌿','🍃','🌱','🌲',
  '🍎','🍊','🍋','🍇','🍓','🍒','🍑','🥭','🍍','🥝',
  '🏥','💊','💉','🩺','🩻','🩹','🏨','✈️','🚗','🚀',
  '📱','💻','📷','🎥','📚','📝','✏️','🔍','💡','🔔',
];

const ICONS = [
  { icon: 'ri-heart-line', label: '❤' },
  { icon: 'ri-star-line', label: '★' },
  { icon: 'ri-check-line', label: '✓' },
  { icon: 'ri-close-line', label: '✗' },
  { icon: 'ri-arrow-right-line', label: '→' },
  { icon: 'ri-arrow-left-line', label: '←' },
  { icon: 'ri-arrow-up-line', label: '↑' },
  { icon: 'ri-arrow-down-line', label: '↓' },
  { icon: 'ri-information-line', label: 'ℹ' },
  { icon: 'ri-warning-line', label: '⚠' },
  { icon: 'ri-phone-line', label: '📞' },
  { icon: 'ri-mail-line', label: '✉' },
  { icon: 'ri-map-pin-line', label: '📍' },
  { icon: 'ri-time-line', label: '⏰' },
  { icon: 'ri-calendar-line', label: '📅' },
  { icon: 'ri-user-line', label: '👤' },
  { icon: 'ri-team-line', label: '👥' },
  { icon: 'ri-hospital-line', label: '🏥' },
  { icon: 'ri-stethoscope-line', label: '🩺' },
  { icon: 'ri-medicine-bottle-line', label: '💊' },
  { icon: 'ri-global-line', label: '🌐' },
  { icon: 'ri-translate-2', label: '🌍' },
  { icon: 'ri-flight-takeoff-line', label: '✈' },
  { icon: 'ri-hotel-line', label: '🏨' },
];

type ToolbarPanel = 'emoji' | 'icon' | 'image' | 'link' | null;

export default function RichTextEditor({ value, onChange, placeholder, lang = 'uz' }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activePanel, setActivePanel] = useState<ToolbarPanel>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [emojiSearch, setEmojiSearch] = useState('');
  const [savedRange, setSavedRange] = useState<Range | null>(null);
  const isInitialized = useRef(false);

  const t = (uz: string, ru: string) => lang === 'uz' ? uz : ru;

  // Initialize editor with value
  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value || '';
      isInitialized.current = true;
    }
  }, []);

  // Sync external value changes (e.g., when editing a post)
  useEffect(() => {
    if (editorRef.current && isInitialized.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      setSavedRange(sel.getRangeAt(0).cloneRange());
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (savedRange) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedRange);
      }
    }
  }, [savedRange]);

  const execCmd = useCallback((command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const insertHTML = useCallback((html: string) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand('insertHTML', false, html);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange, restoreSelection]);

  const handleInsertEmoji = (emoji: string) => {
    insertHTML(emoji);
    setActivePanel(null);
  };

  const handleInsertIcon = (iconClass: string, label: string) => {
    const html = `<i class="${iconClass}" title="${label}" style="font-size:1.1em;vertical-align:middle;margin:0 2px;"></i>`;
    insertHTML(html);
    setActivePanel(null);
  };

  const handleInsertImage = () => {
    if (!imageUrl.trim()) return;
    const html = `<figure style="margin:12px 0;text-align:center;"><img src="${imageUrl.trim()}" alt="${imageAlt || ''}" style="max-width:100%;border-radius:8px;display:inline-block;" />${imageAlt ? `<figcaption style="font-size:0.8em;color:#888;margin-top:4px;">${imageAlt}</figcaption>` : ''}</figure>`;
    insertHTML(html);
    setImageUrl('');
    setImageAlt('');
    setActivePanel(null);
  };

  const handleInsertLink = () => {
    if (!linkUrl.trim()) return;
    const text = linkText.trim() || linkUrl.trim();
    const html = `<a href="${linkUrl.trim()}" target="_blank" rel="noopener noreferrer" style="color:#0d9488;text-decoration:underline;">${text}</a>`;
    insertHTML(html);
    setLinkUrl('');
    setLinkText('');
    setActivePanel(null);
  };

  const handleInsertDivider = () => {
    insertHTML('<hr style="border:none;border-top:2px solid #e5e7eb;margin:16px 0;" /><p><br></p>');
  };

  const handleInsertQuote = () => {
    insertHTML('<blockquote style="border-left:4px solid #14b8a6;padding:8px 16px;margin:12px 0;background:#f0fdfa;color:#374151;border-radius:0 8px 8px 0;font-style:italic;"><br></blockquote><p><br></p>');
  };

  const handleInsertList = (ordered: boolean) => {
    execCmd(ordered ? 'insertOrderedList' : 'insertUnorderedList');
  };

  const handleInsertTable = () => {
    const html = `<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:0.9em;">
      <thead><tr>
        <th style="border:1px solid #d1d5db;padding:8px 12px;background:#f9fafb;text-align:left;">${t('Ustun 1', 'Столбец 1')}</th>
        <th style="border:1px solid #d1d5db;padding:8px 12px;background:#f9fafb;text-align:left;">${t('Ustun 2', 'Столбец 2')}</th>
        <th style="border:1px solid #d1d5db;padding:8px 12px;background:#f9fafb;text-align:left;">${t('Ustun 3', 'Столбец 3')}</th>
      </tr></thead>
      <tbody>
        <tr>
          <td style="border:1px solid #d1d5db;padding:8px 12px;">${t('Ma\'lumot', 'Данные')}</td>
          <td style="border:1px solid #d1d5db;padding:8px 12px;">${t('Ma\'lumot', 'Данные')}</td>
          <td style="border:1px solid #d1d5db;padding:8px 12px;">${t('Ma\'lumot', 'Данные')}</td>
        </tr>
      </tbody>
    </table><p><br></p>`;
    insertHTML(html);
  };

  const handleInsertCallout = (type: 'info' | 'warning' | 'success') => {
    const styles = {
      info: { bg: '#eff6ff', border: '#3b82f6', icon: 'ri-information-line', color: '#1d4ed8' },
      warning: { bg: '#fffbeb', border: '#f59e0b', icon: 'ri-alert-line', color: '#92400e' },
      success: { bg: '#f0fdf4', border: '#22c55e', icon: 'ri-checkbox-circle-line', color: '#166534' },
    };
    const s = styles[type];
    const html = `<div style="background:${s.bg};border-left:4px solid ${s.border};padding:12px 16px;margin:12px 0;border-radius:0 8px 8px 0;display:flex;gap:10px;align-items:flex-start;"><i class="${s.icon}" style="color:${s.color};font-size:1.2em;margin-top:2px;flex-shrink:0;"></i><div style="color:${s.color};">${t('Matn kiriting...', 'Введите текст...')}</div></div><p><br></p>`;
    insertHTML(html);
  };

  const togglePanel = (panel: ToolbarPanel) => {
    saveSelection();
    setActivePanel(prev => prev === panel ? null : panel);
  };

  const filteredEmojis = emojiSearch
    ? EMOJI_LIST.filter((_, i) => i % 3 === 0).slice(0, 40)
    : EMOJI_LIST;

  const ToolBtn = ({ onClick, icon, title, active }: { onClick: () => void; icon: string; title: string; active?: boolean }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className={`w-8 h-8 flex items-center justify-center rounded text-sm transition-colors cursor-pointer ${active ? 'bg-teal-100 text-teal-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
    >
      <i className={icon}></i>
    </button>
  );

  return (
    <div className="border border-gray-200 rounded-xl overflow-visible bg-white shadow-sm">
      {/* Toolbar */}
      <div className="border-b border-gray-100 bg-gray-50/80 px-3 py-2 flex flex-wrap gap-1 items-center rounded-t-xl">
        {/* Text format */}
        <ToolBtn onClick={() => execCmd('bold')} icon="ri-bold" title={t('Qalin', 'Жирный')} />
        <ToolBtn onClick={() => execCmd('italic')} icon="ri-italic" title={t('Kursiv', 'Курсив')} />
        <ToolBtn onClick={() => execCmd('underline')} icon="ri-underline" title={t('Tagiga chizish', 'Подчёркнутый')} />
        <ToolBtn onClick={() => execCmd('strikeThrough')} icon="ri-strikethrough" title={t('Chizib o\'tish', 'Зачёркнутый')} />

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Headings */}
        <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd('formatBlock', 'h2'); }} title="H2" className="px-2 h-8 flex items-center justify-center rounded text-xs font-bold text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap">H2</button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd('formatBlock', 'h3'); }} title="H3" className="px-2 h-8 flex items-center justify-center rounded text-xs font-bold text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap">H3</button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd('formatBlock', 'p'); }} title="P" className="px-2 h-8 flex items-center justify-center rounded text-xs text-gray-600 hover:bg-gray-100 cursor-pointer whitespace-nowrap">P</button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Alignment */}
        <ToolBtn onClick={() => execCmd('justifyLeft')} icon="ri-align-left" title={t('Chapga', 'По левому краю')} />
        <ToolBtn onClick={() => execCmd('justifyCenter')} icon="ri-align-center" title={t('Markazga', 'По центру')} />
        <ToolBtn onClick={() => execCmd('justifyRight')} icon="ri-align-right" title={t('O\'ngga', 'По правому краю')} />

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Lists */}
        <ToolBtn onClick={() => handleInsertList(false)} icon="ri-list-unordered" title={t('Tartibsiz ro\'yxat', 'Маркированный список')} />
        <ToolBtn onClick={() => handleInsertList(true)} icon="ri-list-ordered" title={t('Tartibli ro\'yxat', 'Нумерованный список')} />

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Color */}
        <div className="relative flex items-center gap-0.5">
          {['#ef4444','#f97316','#eab308','#22c55e','#14b8a6','#6366f1','#ec4899','#000000'].map(color => (
            <button
              key={color}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); execCmd('foreColor', color); }}
              title={color}
              className="w-5 h-5 rounded-full border border-white shadow-sm cursor-pointer hover:scale-110 transition-transform"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Special inserts */}
        <ToolBtn onClick={handleInsertDivider} icon="ri-separator" title={t('Ajratuvchi chiziq', 'Разделитель')} />
        <ToolBtn onClick={handleInsertQuote} icon="ri-double-quotes-l" title={t('Iqtibos', 'Цитата')} />
        <ToolBtn onClick={handleInsertTable} icon="ri-table-line" title={t('Jadval', 'Таблица')} />

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Callouts */}
        <button type="button" onMouseDown={(e) => { e.preventDefault(); handleInsertCallout('info'); }} title={t('Ma\'lumot bloki', 'Блок информации')} className="w-8 h-8 flex items-center justify-center rounded text-sm text-blue-500 hover:bg-blue-50 cursor-pointer">
          <i className="ri-information-line"></i>
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); handleInsertCallout('warning'); }} title={t('Ogohlantirish bloki', 'Блок предупреждения')} className="w-8 h-8 flex items-center justify-center rounded text-sm text-amber-500 hover:bg-amber-50 cursor-pointer">
          <i className="ri-alert-line"></i>
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); handleInsertCallout('success'); }} title={t('Muvaffaqiyat bloki', 'Блок успеха')} className="w-8 h-8 flex items-center justify-center rounded text-sm text-green-500 hover:bg-green-50 cursor-pointer">
          <i className="ri-checkbox-circle-line"></i>
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Emoji */}
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); togglePanel('emoji'); }}
          title={t('Emoji', 'Эмодзи')}
          className={`w-8 h-8 flex items-center justify-center rounded text-base cursor-pointer transition-colors ${activePanel === 'emoji' ? 'bg-teal-100' : 'hover:bg-gray-100'}`}
        >
          😊
        </button>

        {/* Icons */}
        <ToolBtn onClick={() => togglePanel('icon')} icon="ri-remixicon-line" title={t('Ikonka', 'Иконка')} active={activePanel === 'icon'} />

        {/* Image */}
        <ToolBtn onClick={() => togglePanel('image')} icon="ri-image-add-line" title={t('Rasm qo\'shish', 'Добавить изображение')} active={activePanel === 'image'} />

        {/* Link */}
        <ToolBtn onClick={() => togglePanel('link')} icon="ri-link" title={t('Havola qo\'shish', 'Добавить ссылку')} active={activePanel === 'link'} />

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Undo/Redo */}
        <ToolBtn onClick={() => execCmd('undo')} icon="ri-arrow-go-back-line" title={t('Orqaga', 'Отменить')} />
        <ToolBtn onClick={() => execCmd('redo')} icon="ri-arrow-go-forward-line" title={t('Oldinga', 'Повторить')} />
      </div>

      {/* Panels */}
      {activePanel === 'emoji' && (
        <div className="border-b border-gray-100 bg-white p-3">
          <input
            type="text"
            value={emojiSearch}
            onChange={(e) => setEmojiSearch(e.target.value)}
            placeholder={t('Emoji qidirish...', 'Поиск эмодзи...')}
            className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm mb-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
          <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
            {filteredEmojis.map((emoji, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleInsertEmoji(emoji); }}
                className="w-9 h-9 flex items-center justify-center text-xl hover:bg-gray-100 rounded cursor-pointer transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {activePanel === 'icon' && (
        <div className="border-b border-gray-100 bg-white p-3">
          <p className="text-xs text-gray-500 mb-2">{t('Ikonka tanlang:', 'Выберите иконку:')}</p>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {ICONS.map((item, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleInsertIcon(item.icon, item.label); }}
                title={item.label}
                className="w-10 h-10 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-teal-50 hover:border-teal-300 cursor-pointer transition-colors"
              >
                <i className={`${item.icon} text-lg text-gray-600`}></i>
              </button>
            ))}
          </div>
        </div>
      )}

      {activePanel === 'image' && (
        <div className="border-b border-gray-100 bg-white p-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">{t('Rasm URL manzili', 'URL изображения')} *</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">{t('Alt matn (ixtiyoriy)', 'Alt текст (необязательно)')}</label>
              <input
                type="text"
                value={imageAlt}
                onChange={(e) => setImageAlt(e.target.value)}
                placeholder={t('Rasm tavsifi...', 'Описание изображения...')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleInsertImage(); }}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('Qo\'shish', 'Добавить')}
            </button>
          </div>
        </div>
      )}

      {activePanel === 'link' && (
        <div className="border-b border-gray-100 bg-white p-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">URL *</label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">{t('Havola matni', 'Текст ссылки')}</label>
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder={t('Havola matni...', 'Текст ссылки...')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleInsertLink(); }}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('Qo\'shish', 'Добавить')}
            </button>
          </div>
        </div>
      )}

      {/* Editor Area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        data-placeholder={placeholder}
        className="min-h-[280px] p-4 text-sm text-gray-800 focus:outline-none leading-relaxed rich-editor-content"
        style={{ wordBreak: 'break-word' }}
      />

      {/* Footer */}
      <div className="border-t border-gray-100 px-4 py-2 flex items-center justify-between bg-gray-50/50 rounded-b-xl">
        <span className="text-xs text-gray-400">
          {t('Matn uzunligi cheklanmagan', 'Длина текста не ограничена')}
        </span>
        <span className="text-xs text-gray-400">
          {t('Ctrl+B qalin, Ctrl+I kursiv', 'Ctrl+B жирный, Ctrl+I курсив')}
        </span>
      </div>

      <style>{`
        .rich-editor-content:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rich-editor-content h2 { font-size: 1.4em; font-weight: 700; margin: 12px 0 6px; color: #111827; }
        .rich-editor-content h3 { font-size: 1.15em; font-weight: 600; margin: 10px 0 4px; color: #1f2937; }
        .rich-editor-content p { margin: 4px 0; }
        .rich-editor-content ul { list-style: disc; padding-left: 1.5em; margin: 6px 0; }
        .rich-editor-content ol { list-style: decimal; padding-left: 1.5em; margin: 6px 0; }
        .rich-editor-content a { color: #0d9488; text-decoration: underline; }
        .rich-editor-content blockquote { border-left: 4px solid #14b8a6; padding: 8px 16px; margin: 12px 0; background: #f0fdfa; border-radius: 0 8px 8px 0; font-style: italic; }
        .rich-editor-content table { width: 100%; border-collapse: collapse; margin: 12px 0; }
        .rich-editor-content td, .rich-editor-content th { border: 1px solid #d1d5db; padding: 8px 12px; }
        .rich-editor-content th { background: #f9fafb; font-weight: 600; }
        .rich-editor-content img { max-width: 100%; border-radius: 8px; }
        .rich-editor-content hr { border: none; border-top: 2px solid #e5e7eb; margin: 16px 0; }
      `}</style>
    </div>
  );
}
